import { useState, useEffect, useMemo } from "react";
import {
  Button,
  MenuItem,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Checkbox,
  FormControlLabel,
  Slider,
  Grid,
  TextField,
  RadioGroup,
  Radio,
  Select,
  Chip,
  FormGroup,
  IconButton,
  Box,
  Drawer,
  InputAdornment,
  Badge,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { BiDollar } from "react-icons/bi";
import {
  broadFieldApi,
  fetchCoursesApi,
  fetchEnglishTestsApi,
  fetchIntakesApi,
  filterCoursesApi,
  narrowFieldApi,
} from "../../api/coursesApi";

const courseEndPoint = process.env.REACT_APP_API_COURSE_FILTER;

export default function FiltersDropdown({ onData, selectedCountries }) {
  const durationOptions = [
    { label: "6 Months", value: 6 },
    { label: "9 Months", value: 9 },
    { label: "1 Year", value: 12 },
    { label: "1.5 Years", value: 18 },
    { label: "2 Years", value: 24 },
    { label: "3 Years", value: 36 },
    { label: "4 Years", value: 48 },
    { label: "5 Years", value: 60 },
  ];
  const [budget, setBudget] = useState([3000, 99980]);
  const [durations, setDurations] = useState([]);
  const [currency, setCurrency] = useState("USD");
  const [open, setOpen] = useState(false);
  const [broadFields, setBroadFields] = useState([]);
  const [narrowFields, setNarrowFields] = useState({});
  const [checked, setChecked] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
  const [studyLevel, setStudyLevel] = useState();
  const [courseLevels, setCourseLevels] = useState({
    underGraduate: [],
    postGraduate: [],
  });
  const [selectedLevels, setSelectedLevels] = useState([]);
  const [year, setYear] = useState([]);
  const [intakes, setIntakes] = useState([]);
  const [intake, setIntake] = useState("");
  const [tests, setTests] = useState([]);
  const [testOptions, setTestOptions] = useState([]);
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);
  const [loading, setLoading] = useState(false);
  const [checkedLevels, setCheckedLevels] = useState([]);
  const [resetting, setResetting] = useState(false);
  const [selectedBroad, setSelectedBroad] = useState("");
  const activeFilterCount = useMemo(() => {
    let count = 0;

    // 1 Field of Study — any broad or narrow field selected
    const hasFieldSelected = Object.values(checked).some((val) => val === true);
    if (hasFieldSelected) count++;

    // 2 Level of Study — if level or checkedLevels selected
    if (studyLevel || (checkedLevels && checkedLevels.length > 0)) count++;

    // 3 Annual Budget — if changed from default range
    const defaultBudget = [3000, 99980]; // or whatever your default is
    if (budget[0] !== defaultBudget[0] || budget[1] !== defaultBudget[1])
      count++;

    // 4 Intake Period — if intake is selected
    if (intake) count++;

    // 5 Duration — if any duration selected
    if (durations.length > 0) count++;

    // 6 Entrance Test — if any test selected
    if (tests.length > 0) count++;

    return count;
  }, [checked, studyLevel, checkedLevels, budget, intake, durations, tests]);

  useEffect(() => {
    const initialBroadFieldId = localStorage.getItem("broadFieldId");
    const initialNarrowFieldId = localStorage.getItem("narrowFieldId");

    // If data isn’t ready yet, wait
    if (!broadFields.length || !Object.keys(narrowFields).length) return;

    // 1 Find matching broad field
    const selectedBroad = broadFields.find(
      (b) => String(b.id) === String(initialBroadFieldId),
    );
    if (!selectedBroad) return;

    // 2 Find matching narrow field inside that broad field
    const subFields = narrowFields[selectedBroad.id] || [];
    const selectedNarrow = subFields.find(
      (n) => String(n.id) === String(initialNarrowFieldId),
    );
    if (!selectedNarrow) return;

    // 3 Mark both as checked
    setChecked((prev) => ({
      ...prev,
      [selectedBroad.broad_field_name]: "indeterminate",
      [selectedNarrow.narrow_field_name]: true,
    }));

    // 4 Inform parent (optional)
    onData?.({
      broadFieldId: selectedBroad.id,
      narrowFieldId: selectedNarrow.id,
    });
  }, [broadFields, narrowFields]);

  const handleApplyFilters = async () => {
    handleClose();
    try {
      setLoading(true);

      const payload = {
        course_level: checkedLevels.length > 0 ? checkedLevels : selectedLevels,
        broad_field_id: Object.keys(checked)
          .map((fieldName) => {
            const broad = broadFields.find(
              (b) => b.broad_field_name === fieldName,
            );
            return broad ? broad.id : null;
          })
          .filter(Boolean),
        narrow_field_id: Object.values(narrowFields)
          .flat()
          .filter((nf) => checked[nf.narrow_field_name])
          .map((nf) => nf.id),
        annual_budget: [budget[0], budget[1]],
        intake_year_from: year,
        intake_period: [intake] || null,
        duration: durations,
        tests: tests,
        country_id: selectedCountries || [],
        currency,
      };

      const url = `${courseEndPoint}get-courses-filters`;

      const response = await filterCoursesApi({ url, payload });
      const data = response?.data?.data || [];
      onData(data);
    } catch (err) {
      console.error("Error applying filters:", err);
    } finally {
      setLoading(false);
    }
  };

  const FetchBroadField = async () => {
    try {
      const response = await broadFieldApi();
      if (response.data.success) {
        const data = response.data.data;
        setBroadFields(data);

        // If user had a stored broad field, fetch its narrow fields too
        const storedBroadId = localStorage.getItem("broadFieldId");
        if (storedBroadId) {
          await fetchNarrowFields(storedBroadId);
        }
      }
    } catch (err) {
      console.error("Failed to fetch broad fields:", err);
    }
  };

  const fetchNarrowFields = async (broadFieldId) => {
    try {
      const res = await narrowFieldApi({ broad_field_id: broadFieldId });

      setNarrowFields((prev) => ({ ...prev, [broadFieldId]: res.data.data }));
    } catch (err) {
      console.error("Error fetching narrow fields:", err);
    }
  };

  // fetch levels API
  const fetchCourseLevels = async () => {
    try {
      const res = await fetchCoursesApi();
      if (res.data.success) {
        setCourseLevels(res.data.data);
      }
    } catch (err) {
      console.error("Failed to fetch course levels", err);
    }
  };

  const fetchIntakes = async () => {
    try {
      const res = await fetchIntakesApi();
      if (res.data.success) {
        setIntakes(res.data.data);
      }
    } catch (err) {
      console.error("Failed to fetch intakes", err);
    }
  };

  // fetch tests API
  const fetchTests = async () => {
    try {
      const res = await fetchEnglishTestsApi();
      if (res.data.success) {
        setTestOptions(res.data.data.map((t) => t.name));
      }
    } catch (err) {
      console.error("Failed to fetch tests", err);
    }
  };

  useEffect(() => {
    fetchIntakes();
    fetchTests();
    fetchCourseLevels();
    FetchBroadField();
  }, []);

  // Toggle parent
  const handleParentChange = (broad) => (event) => {
    const isChecked = event.target.checked;
    const parentKey = broad.broad_field_name;

    setChecked((prev) => {
      const updated = { ...prev, [parentKey]: isChecked };

      if (narrowFields[broad.id]) {
        narrowFields[broad.id].forEach((nf) => {
          updated[nf.narrow_field_name] = isChecked;
        });
      }
      return updated;
    });

    if (isChecked && !narrowFields[broad.id]) {
      fetchNarrowFields(broad.id);
    }
  };

  // Toggle child
  const handleChildChange = (parent, broadId, child, subFields) => (event) => {
    const isChecked = event.target.checked;

    setChecked((prev) => {
      const updated = { ...prev, [child]: isChecked };

      const allSelected = subFields.every(
        (sf) => updated[sf.narrow_field_name],
      );
      const noneSelected = subFields.every(
        (sf) => !updated[sf.narrow_field_name],
      );

      updated[parent] = allSelected
        ? true
        : noneSelected
          ? false
          : "indeterminate";
      return updated;
    });
  };

  const getParentProps = (broad) => {
    const val = checked[broad.broad_field_name];
    return {
      checked: val === true,
      indeterminate: val === "indeterminate",
      onChange: handleParentChange(broad),
    };
  };

  // 3. Apply search filter
  const filteredBroadFields = broadFields.filter((broad) => {
    const matchesBroad = broad.broad_field_name
      .toLowerCase()
      .includes(searchTerm.toLowerCase());

    const subFields = narrowFields[broad.id] || [];
    const matchesNarrow = subFields.some((nf) =>
      nf.narrow_field_name.toLowerCase().includes(searchTerm.toLowerCase()),
    );

    return matchesBroad || matchesNarrow;
  });

  // icons + colors map
  const intakeMeta = {
    Winter: {
      icon: (
        <svg
          width="20"
          height="21"
          viewBox="0 0 20 21"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <g clip-path="url(#clip0_6855_24435)">
            <g filter="url(#filter0_d_6855_24435)">
              <path
                d="M18.1803 14.3044C18.2678 14.6378 18.0741 14.9838 17.7366 15.0719L16.0303 15.5311C15.9803 15.5436 15.9241 15.5499 15.8678 15.5499C15.7616 15.5499 15.6491 15.5185 15.5553 15.4682C15.4116 15.3864 15.3053 15.248 15.2616 15.0845L14.8678 13.6188L13.6928 12.9331L13.7616 13.1848C13.8491 13.5181 13.6553 13.8641 13.3178 13.9522C13.2616 13.9648 13.2116 13.9711 13.1553 13.9711C12.8803 13.9711 12.6303 13.7886 12.5553 13.5056L12.1678 12.0399L11.0803 11.4109L10.6241 11.6876V12.952L11.6928 14.0277C11.9366 14.273 11.9366 14.6693 11.6928 14.9146C11.5678 15.0341 11.4116 15.097 11.2491 15.097C11.0866 15.097 10.9303 15.0341 10.8053 14.9146L10.6241 14.7259V16.0909L11.6928 17.1666C11.9366 17.4119 11.9366 17.8082 11.6928 18.0535L10.4428 19.3116C10.3178 19.4374 10.1616 19.5003 9.99909 19.5003C9.83659 19.5003 9.68034 19.4374 9.55534 19.3179L8.30534 18.0598C8.06159 17.8145 8.06159 17.4182 8.30534 17.1729L9.37409 16.0972V14.7322L9.19284 14.9146C8.94909 15.1599 8.55534 15.1599 8.31159 14.9146C8.06784 14.6693 8.06784 14.273 8.31159 14.0277L9.37409 12.952V11.6876L8.91784 11.4234L7.83034 12.0525L7.44284 13.5181C7.36784 13.8012 7.11784 13.9836 6.83659 13.9836C6.78034 13.9836 6.73034 13.9773 6.67409 13.9648C6.34284 13.8767 6.14284 13.5307 6.23034 13.1973L6.29909 12.9457L5.12409 13.6251L4.73659 15.0907C4.69284 15.2543 4.58659 15.3864 4.44284 15.4745C4.34909 15.5311 4.23659 15.5562 4.13034 15.5562C4.07409 15.5562 4.02409 15.5499 3.96784 15.5374L2.26159 15.0782C1.93034 14.9901 1.73034 14.6441 1.81784 14.3107L2.27409 12.5935C2.31784 12.4299 2.42409 12.2978 2.56784 12.2097C2.71159 12.128 2.88034 12.1028 3.04284 12.1468L4.49909 12.5431L5.67409 11.8575L5.42409 11.7883C5.09284 11.7002 4.89284 11.3543 4.98034 11.0209C5.06784 10.6875 5.41159 10.4862 5.74284 10.5742L7.19909 10.9705L8.28659 10.3415V9.80681L7.19909 9.17778L5.74909 9.56149C5.69284 9.57407 5.64284 9.58036 5.58659 9.58036C5.31159 9.58036 5.05534 9.39794 4.98034 9.11487C4.89284 8.78148 5.08659 8.43551 5.42409 8.34744L5.67409 8.27825L4.49909 7.5926L3.04284 7.98889C2.99284 8.00147 2.93659 8.00776 2.88034 8.00776C2.77409 8.00776 2.66159 7.97631 2.56784 7.92599C2.42409 7.84421 2.31784 7.70582 2.27409 7.54227L1.81784 5.825C1.73034 5.49161 1.92409 5.14564 2.26159 5.05128L3.96784 4.59208C4.12409 4.54805 4.29909 4.57321 4.44284 4.65499C4.58659 4.73676 4.69284 4.87515 4.73659 5.0387L5.12409 6.50436L6.29909 7.19001L6.23034 6.9384C6.14284 6.60501 6.33659 6.25903 6.67409 6.17097C7.00534 6.07661 7.34909 6.27791 7.43659 6.61759L7.82409 8.08325L8.91159 8.71229L9.37409 8.4418V7.17743L8.30534 6.10177C8.06159 5.85645 8.06159 5.46016 8.30534 5.21483C8.54909 4.9695 8.94284 4.9695 9.18659 5.21483L9.37409 5.40354V4.03224L8.30534 2.95658C8.06159 2.71125 8.06159 2.31496 8.30534 2.06964L9.55534 0.811557C9.79909 0.566232 10.1928 0.566232 10.4366 0.811557L11.6866 2.06964C11.9303 2.31496 11.9303 2.71125 11.6866 2.95658L10.6241 4.03224V5.39725L10.8053 5.21483C11.0491 4.9695 11.4428 4.9695 11.6866 5.21483C11.9303 5.46016 11.9303 5.85645 11.6866 6.10177L10.6241 7.17743V8.4418L11.0803 8.706L12.1678 8.07696L12.5553 6.6113C12.6428 6.27791 12.9866 6.07661 13.3178 6.16468C13.6491 6.25274 13.8491 6.59872 13.7616 6.93211L13.6928 7.18372L14.8678 6.49807L15.2616 5.03241C15.3053 4.86886 15.4116 4.73676 15.5553 4.64869C15.6991 4.56692 15.8678 4.54176 16.0303 4.58579L17.7366 5.04499C18.0678 5.13306 18.2678 5.47903 18.1803 5.81242L17.7241 7.52969C17.6803 7.69324 17.5741 7.82534 17.4303 7.91341C17.3366 7.97002 17.2241 7.99518 17.1178 7.99518C17.0616 7.99518 17.0116 7.98889 16.9553 7.97631L15.4991 7.58002L14.3241 8.26567L14.5741 8.33486C14.9053 8.42293 15.1053 8.7689 15.0178 9.10229C14.9428 9.38536 14.6928 9.56778 14.4178 9.56778C14.3616 9.56778 14.3116 9.56149 14.2553 9.54891L12.7991 9.15261L11.7116 9.78165V10.3163L12.7991 10.9454L14.2553 10.5491C14.5866 10.461 14.9303 10.656 15.0178 10.9957C15.1053 11.3291 14.9116 11.6751 14.5741 11.7631L14.3241 11.8323L15.4991 12.518L16.9553 12.1217C17.1116 12.0776 17.2866 12.1028 17.4303 12.1846C17.5741 12.2664 17.6803 12.4047 17.7241 12.5683L18.1803 14.3044Z"
                fill="url(#paint0_linear_6855_24435)"
              />
            </g>
          </g>
          <defs>
            <filter
              id="filter0_d_6855_24435"
              x="-8.20312"
              y="-5.37244"
              width="36.4062"
              height="38.8727"
              filterUnits="userSpaceOnUse"
              color-interpolation-filters="sRGB"
            >
              <feFlood flood-opacity="0" result="BackgroundImageFix" />
              <feColorMatrix
                in="SourceAlpha"
                type="matrix"
                values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                result="hardAlpha"
              />
              <feOffset dy="4" />
              <feGaussianBlur stdDeviation="5" />
              <feComposite in2="hardAlpha" operator="out" />
              <feColorMatrix
                type="matrix"
                values="0 0 0 0 0.0901961 0 0 0 0 0.486275 0 0 0 0 0.823529 0 0 0 0.2 0"
              />
              <feBlend
                mode="normal"
                in2="BackgroundImageFix"
                result="effect1_dropShadow_6855_24435"
              />
              <feBlend
                mode="normal"
                in="SourceGraphic"
                in2="effect1_dropShadow_6855_24435"
                result="shape"
              />
            </filter>
            <linearGradient
              id="paint0_linear_6855_24435"
              x1="9.99909"
              y1="0.629136"
              x2="9.99909"
              y2="19.5003"
              gradientUnits="userSpaceOnUse"
            >
              <stop stop-color="#00BCD4" />
              <stop offset="1" stop-color="#1976D2" />
            </linearGradient>
            <clipPath id="clip0_6855_24435">
              <rect width="20" height="20.1292" fill="white" />
            </clipPath>
          </defs>
        </svg>
      ),
      color: "#3b82f6",
    },
    Summer: {
      icon: (
        <svg
          width="20"
          height="21"
          viewBox="0 0 20 21"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <g clip-path="url(#clip0_6855_24422)">
            <g filter="url(#filter0_d_6855_24422)">
              <path
                d="M9.375 2.5148V1.25672C9.375 0.910753 9.65625 0.627686 10 0.627686C10.3438 0.627686 10.625 0.910753 10.625 1.25672V2.5148C10.625 2.86077 10.3438 3.14384 10 3.14384C9.65625 3.14384 9.375 2.86077 9.375 2.5148ZM4.25625 5.16935C4.375 5.29516 4.5375 5.35177 4.69375 5.35177C4.85 5.35177 5.0125 5.28887 5.1375 5.16935C5.38125 4.92402 5.38125 4.52773 5.1375 4.2824L4.25625 3.38917C4.0125 3.14384 3.6125 3.14384 3.36875 3.38917C3.125 3.63449 3.125 4.03708 3.36875 4.2824L4.25625 5.16935ZM1.25 10.6923H2.5C2.84375 10.6923 3.125 10.4092 3.125 10.0633C3.125 9.7173 2.84375 9.43423 2.5 9.43423H1.25C0.90625 9.43423 0.625 9.7173 0.625 10.0633C0.625 10.4092 0.90625 10.6923 1.25 10.6923ZM17.5 10.6923H18.75C19.0938 10.6923 19.375 10.4092 19.375 10.0633C19.375 9.7173 19.0938 9.43423 18.75 9.43423H17.5C17.1562 9.43423 16.875 9.7173 16.875 10.0633C16.875 10.4092 17.1562 10.6923 17.5 10.6923ZM15.3062 5.35177C15.4688 5.35177 15.625 5.28887 15.75 5.16935L16.6313 4.2824C16.875 4.03708 16.875 3.64078 16.6313 3.39546C16.3875 3.15013 15.9938 3.15013 15.75 3.39546L14.8688 4.2824C14.625 4.52773 14.625 4.92402 14.8688 5.16935C14.9813 5.29516 15.1438 5.35177 15.3062 5.35177ZM18.75 13.2085H14.6687C15.2812 12.2838 15.625 11.1955 15.625 10.0633C15.625 6.94324 13.1 4.40192 10 4.40192C6.9 4.40192 4.375 6.94324 4.375 10.0633C4.375 11.1955 4.71875 12.2838 5.33125 13.2085H1.25C0.90625 13.2085 0.625 13.4915 0.625 13.8375C0.625 14.1835 0.90625 14.4665 1.25 14.4665H18.75C19.0938 14.4665 19.375 14.1835 19.375 13.8375C19.375 13.4915 19.0938 13.2085 18.75 13.2085ZM10 18.2408H3.125C2.78125 18.2408 2.5 18.5238 2.5 18.8698C2.5 19.2158 2.78125 19.4989 3.125 19.4989H10C10.3438 19.4989 10.625 19.2158 10.625 18.8698C10.625 18.5238 10.3438 18.2408 10 18.2408ZM16.875 18.2408H12.5C12.1562 18.2408 11.875 18.5238 11.875 18.8698C11.875 19.2158 12.1562 19.4989 12.5 19.4989H16.875C17.2188 19.4989 17.5 19.2158 17.5 18.8698C17.5 18.5238 17.2188 18.2408 16.875 18.2408ZM15 16.9827C15.3438 16.9827 15.625 16.6996 15.625 16.3537C15.625 16.0077 15.3438 15.7246 15 15.7246H9.375C9.03125 15.7246 8.75 16.0077 8.75 16.3537C8.75 16.6996 9.03125 16.9827 9.375 16.9827H15ZM6.875 16.9827C7.21875 16.9827 7.5 16.6996 7.5 16.3537C7.5 16.0077 7.21875 15.7246 6.875 15.7246H5C4.65625 15.7246 4.375 16.0077 4.375 16.3537C4.375 16.6996 4.65625 16.9827 5 16.9827H6.875Z"
                fill="url(#paint0_linear_6855_24422)"
              />
            </g>
          </g>
          <defs>
            <filter
              id="filter0_d_6855_24422"
              x="-9.375"
              y="-5.37231"
              width="38.75"
              height="38.8712"
              filterUnits="userSpaceOnUse"
              color-interpolation-filters="sRGB"
            >
              <feFlood flood-opacity="0" result="BackgroundImageFix" />
              <feColorMatrix
                in="SourceAlpha"
                type="matrix"
                values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                result="hardAlpha"
              />
              <feOffset dy="4" />
              <feGaussianBlur stdDeviation="5" />
              <feComposite in2="hardAlpha" operator="out" />
              <feColorMatrix
                type="matrix"
                values="0 0 0 0 0.980392 0 0 0 0 0.596078 0 0 0 0 0.0823529 0 0 0 0.25 0"
              />
              <feBlend
                mode="normal"
                in2="BackgroundImageFix"
                result="effect1_dropShadow_6855_24422"
              />
              <feBlend
                mode="normal"
                in="SourceGraphic"
                in2="effect1_dropShadow_6855_24422"
                result="shape"
              />
            </filter>
            <linearGradient
              id="paint0_linear_6855_24422"
              x1="10"
              y1="0.627686"
              x2="10"
              y2="19.4989"
              gradientUnits="userSpaceOnUse"
            >
              <stop stop-color="#FFC700" />
              <stop offset="1" stop-color="#F77D20" />
            </linearGradient>
            <clipPath id="clip0_6855_24422">
              <rect width="20" height="20.1292" fill="white" />
            </clipPath>
          </defs>
        </svg>
      ),
      color: "#f59e0b",
    },
    Spring: {
      icon: (
        <svg
          width="40"
          height="31"
          viewBox="0 0 40 41"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <g filter="url(#filter0_d_6855_24406)">
            <path
              d="M17.5385 18.6019C19.3073 24.097 21.7774 25.192 24.5787 26.1303L25.0785 25.3009C22.6637 24.6358 21.2251 23.1736 20.8395 21.508C20.3734 19.4948 20.8885 18.0116 20.6282 15.4561C20.3903 13.1247 18.9202 10.3867 15.8775 9.58026C13.7003 9.00385 10 9.16779 10 9.16779C10 9.16779 11.0276 11.9119 11.6691 16.6539C12.0088 19.1655 13.4146 21.3001 15.5832 22.475C16.4926 22.9671 18.4061 23.7406 19.3076 24.097C15.3663 20.5871 16.4001 16.3786 13.9031 13.0484C16.116 14.9836 16.6213 15.7524 17.5385 18.6019Z"
              fill="#76BD1C"
            />
            <path
              d="M26.7031 15.4045C25.9562 18.2219 23.8535 18.343 22.8936 20.6589C24.1058 20.3476 26.5364 19.973 27.665 19.3587C29.188 18.5304 30.0797 17.0937 29.9962 14.8035C29.9467 13.4309 29.056 10.8566 29.056 10.8566C29.056 10.8566 27.4627 12.2134 25.2101 13.5299C23.8063 14.3496 22.2723 15.383 21.9867 17.1302C21.8601 17.9065 21.8203 20.42 21.8203 20.42C23.171 17.2615 24.8096 18.1384 26.7031 15.4045Z"
              fill="#D9E021"
            />
            <path
              d="M22.0693 13.687C22.9188 13.687 23.6074 12.9445 23.6074 12.0286C23.6074 11.1126 22.9188 10.3701 22.0693 10.3701C21.2199 10.3701 20.5312 11.1126 20.5312 12.0286C20.5312 12.9445 21.2199 13.687 22.0693 13.687Z"
              fill="#76BD1C"
            />
            <path
              d="M23.3056 9.20993C23.8885 9.20993 24.3611 8.70036 24.3611 8.07176C24.3611 7.44317 23.8885 6.93359 23.3056 6.93359C22.7226 6.93359 22.25 7.44317 22.25 8.07176C22.25 8.70036 22.7226 9.20993 23.3056 9.20993Z"
              fill="#76BD1C"
            />
            <path
              d="M26.8169 6.51377C26.8723 6.85252 26.6621 7.17361 26.348 7.23209C26.0344 7.29085 25.736 7.06547 25.6806 6.72728C25.6257 6.38936 25.8355 6.06689 26.1501 6.00923C26.4636 5.95075 26.7626 6.17612 26.8169 6.51377Z"
              fill="#76BD1C"
            />
          </g>
          <defs>
            <filter
              id="filter0_d_6855_24406"
              x="0"
              y="0"
              width="40"
              height="40.1302"
              filterUnits="userSpaceOnUse"
              color-interpolation-filters="sRGB"
            >
              <feFlood flood-opacity="0" result="BackgroundImageFix" />
              <feColorMatrix
                in="SourceAlpha"
                type="matrix"
                values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                result="hardAlpha"
              />
              <feOffset dy="4" />
              <feGaussianBlur stdDeviation="5" />
              <feComposite in2="hardAlpha" operator="out" />
              <feColorMatrix
                type="matrix"
                values="0 0 0 0 1 0 0 0 0 1 0 0 0 0 1 0 0 0 0.1 0"
              />
              <feBlend
                mode="normal"
                in2="BackgroundImageFix"
                result="effect1_dropShadow_6855_24406"
              />
              <feBlend
                mode="normal"
                in="SourceGraphic"
                in2="effect1_dropShadow_6855_24406"
                result="shape"
              />
            </filter>
          </defs>
        </svg>
      ),
      color: "#10b981",
    },
    Autumn: {
      icon: (
        <svg
          width="30"
          height="31"
          viewBox="0 0 40 41"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <g filter="url(#filter0_d_6855_24447)">
            <path
              d="M29.807 17.7918C29.7008 17.7643 29.589 17.7538 29.4795 17.7362C29.1288 17.6798 28.7651 17.6582 28.4307 17.5588C28.0221 17.4373 27.6221 17.2752 27.2481 17.0849C26.892 16.9037 26.8599 16.6195 27.1033 16.3273C27.1628 16.2559 27.233 16.1913 27.301 16.1259C27.8837 15.5649 28.4438 14.9876 28.9141 14.3468C29.0171 14.2063 29.1208 14.0627 29.1932 13.909C29.2989 13.6846 29.2108 13.531 28.9715 13.4686C28.3973 13.3189 28.3783 13.3126 28.5919 12.8077C28.8148 12.2808 29.0801 11.7681 29.3249 11.2484C29.3893 11.1119 29.4605 10.976 29.5033 10.8337C29.5784 10.5841 29.4537 10.4644 29.1738 10.5214C28.9248 10.5721 28.6786 10.6389 28.4377 10.7148C28.0913 10.8239 27.7539 10.9555 27.4065 11.0618C27.2403 11.1128 27.0613 11.1359 26.8858 11.1567C26.7304 11.175 26.6302 11.1066 26.6049 10.9638C26.5858 10.8563 26.5702 10.7481 26.546 10.6415C26.4912 10.4002 26.3571 10.3521 26.1634 10.5244C25.9596 10.7056 25.784 10.9118 25.5952 11.1064C25.2832 11.4283 24.9833 11.7605 24.6519 12.0659C24.5194 12.1881 24.3285 12.2833 24.1443 12.3337C23.708 12.4529 23.3331 12.2332 23.2612 11.8344C23.235 11.6894 23.2397 11.5385 23.244 11.3907C23.2627 10.7444 23.4197 10.1163 23.5639 9.48612C23.6125 9.27413 23.6549 9.05715 23.6584 8.84195C23.6624 8.59642 23.5358 8.55318 23.298 8.6845C23.148 8.76737 22.9975 8.85939 22.8326 8.90908C22.5827 8.98435 22.4788 8.91837 22.4453 8.68839C22.4181 8.5023 22.4167 8.31333 22.3927 8.12673C22.3639 7.9017 22.2855 7.82002 22.0362 7.81595C21.7397 7.81105 21.5507 7.68229 21.4298 7.46371C21.2679 7.17114 21.1208 6.87217 20.9679 6.57576C20.8844 6.41396 20.8078 6.24905 20.7152 6.09147C20.6894 6.04745 20.6156 5.99707 20.5677 6C20.5226 6.00275 20.4608 6.06584 20.4446 6.11279C20.409 6.21588 20.4017 6.32638 20.3754 6.43236C20.3155 6.67285 20.2774 6.92113 20.1819 7.15037C20.0574 7.44948 19.7915 7.59375 19.4948 7.57847C19.1941 7.56296 19.1989 7.56392 19.134 7.82798C19.0891 8.01051 19.0508 8.19838 18.9693 8.36874C18.8636 8.58947 18.7219 8.63893 18.4561 8.59619C18.3577 8.58036 18.2623 8.55057 18.1643 8.53273C17.8224 8.47059 17.7398 8.5373 17.769 8.85188C17.7754 8.92061 17.7839 8.9908 17.8061 9.05646C17.9177 9.38737 18.0358 9.7165 18.1484 10.0471C18.2835 10.4439 18.4282 10.8387 18.5438 11.24C18.6165 11.492 18.5954 11.7516 18.487 11.9991C18.3021 12.4207 18.0043 12.5291 17.5341 12.3441C17.1691 12.2005 16.8773 11.9693 16.6187 11.7082C16.2876 11.3738 15.9732 11.0263 15.6514 10.6846C15.5155 10.5404 15.3853 10.3913 15.2414 10.2536C15.0487 10.0692 15.0003 10.0603 14.7499 10.1726C14.5116 10.2795 14.2679 10.3108 14.0046 10.2782C13.2894 10.1894 12.6198 9.98468 11.9901 9.67367C11.9219 9.64 11.8585 9.59694 11.787 9.57063C11.5852 9.49632 11.4748 9.56381 11.5167 9.75521C11.5782 10.0357 11.6569 10.3144 11.7506 10.5878C11.8154 10.7768 11.825 10.9357 11.6243 11.0619C11.4807 11.1522 11.4757 11.2614 11.5502 11.3985C11.7051 11.6833 11.8378 11.9777 11.9947 12.2615C12.1141 12.4773 12.154 12.6539 11.9028 12.8343C11.7129 12.9707 11.7233 13.0338 11.8875 13.2096C12.1502 13.491 12.4207 13.7673 12.7009 14.0352C12.9663 14.2889 13.2628 14.5176 13.5195 14.7777C13.871 15.1338 13.8183 15.436 13.361 15.6857C13.0871 15.8352 12.7958 15.9686 12.4954 16.0695C11.7976 16.3036 11.0887 16.5116 10.3864 16.7351C10.2466 16.7796 10.072 16.7868 10 16.9773C10.0637 17.0158 10.1268 17.0696 10.202 17.0968C10.5837 17.2347 10.9626 17.3819 11.3541 17.4942C11.689 17.5903 11.9896 17.7275 12.2674 17.9157C12.8018 18.2778 12.8891 18.6563 12.5578 19.1774C12.4904 19.2835 12.3338 19.4012 12.4495 19.5177C12.5161 19.5846 12.6914 19.5799 12.8187 19.5838C13.5653 19.6067 14.3128 19.6124 15.0589 19.645C15.4417 19.6618 15.822 19.7226 16.2354 19.7669C16.1643 19.9063 16.1175 20.0056 16.0637 20.1019C15.8158 20.5447 15.563 20.9854 15.3189 21.4299C15.2542 21.5476 15.2139 21.676 15.1398 21.8538C15.3154 21.8419 15.423 21.845 15.5255 21.8261C17.1349 21.53 18.61 20.9928 19.843 20.0039C19.9006 21.9629 20.1934 23.8968 20.7109 25.8069C20.7342 25.893 20.7543 25.9817 20.7956 26.0611C20.8136 26.0959 20.8915 26.1373 20.9243 26.1277C20.9764 26.1125 21.0489 26.0577 21.05 26.0183C21.0506 26.0012 21.0495 25.984 21.0489 25.9668C21.0279 25.9541 21.0095 25.9385 21.0019 25.9239C20.9606 25.8444 20.9406 25.7557 20.9172 25.6696C20.3968 23.7488 20.1038 21.8037 20.0486 19.8333C20.0456 19.727 20.0431 19.6206 20.0414 19.5142C20.0397 19.4007 20.0383 19.2873 20.0382 19.1737C20.0381 19.1351 20.0293 19.0965 20.0216 19.0341C19.8608 19.0109 19.6997 18.9875 19.5385 18.9645C17.7615 18.7106 16.0319 18.2979 14.3441 17.7457C14.02 17.6397 13.6992 17.5258 13.3769 17.4153C13.325 17.3975 13.2733 17.379 13.222 17.36C13.2131 17.3567 13.2044 17.3531 13.196 17.3495C13.1942 17.3487 13.1924 17.3479 13.1906 17.3471C13.1821 17.3432 13.1738 17.3393 13.166 17.3351C13.1659 17.335 13.1657 17.3349 13.1656 17.3348C13.1577 17.3305 13.1502 17.326 13.1431 17.3212C13.1423 17.3207 13.1414 17.3201 13.1406 17.3196C13.1258 17.3094 13.1129 17.2983 13.1027 17.2859C13.1027 17.2859 13.1027 17.2858 13.1026 17.2858C13.0871 17.2668 13.0779 17.2452 13.0774 17.2204C13.077 17.2008 13.0819 17.1792 13.0938 17.1554C13.1465 17.0502 13.2546 17.0708 13.3541 17.1014C13.4172 17.1208 13.4796 17.1421 13.5414 17.1646C15.2382 17.7808 16.9918 18.2293 18.7857 18.5576C19.0919 18.6136 19.4005 18.659 19.7088 18.7054C19.7741 18.7152 19.8393 18.725 19.9045 18.735C19.9348 18.7397 19.968 18.7301 20.0257 18.7247C20.005 18.6816 19.9989 18.642 19.9734 18.6199C19.2366 17.979 18.6498 17.2297 18.0316 16.5037C17.1777 15.501 16.3231 14.4985 15.4866 13.4844C14.82 12.6763 14.1019 11.9081 13.334 11.1751C13.3191 11.1609 13.3045 11.1463 13.2901 11.1314C13.2774 11.1182 13.265 11.1047 13.253 11.091C13.252 11.0899 13.251 11.0888 13.25 11.0876C13.2238 11.0575 13.1995 11.0263 13.1781 10.9938C13.1713 10.9836 13.164 10.9735 13.158 10.963C13.1395 10.9313 13.1597 10.8574 13.1916 10.8339C13.2237 10.8104 13.3057 10.8071 13.3418 10.8278C13.4152 10.87 13.4724 10.9342 13.5362 10.9895C14.4815 11.8098 15.2671 12.746 16.0476 13.6876C16.8537 14.6602 17.6779 15.621 18.4999 16.583C18.8906 17.0402 19.2928 17.4897 19.6941 17.9397C19.7422 17.9936 19.796 18.0439 19.8577 18.0998C19.8579 18.1 19.8581 18.1002 19.8583 18.1003C19.8922 18.1311 19.9284 18.1636 19.9676 18.1994C19.9708 18.2023 19.9738 18.205 19.977 18.208C19.993 18.2226 20.0094 18.2378 20.0264 18.2538C20.0435 18.1361 20.0576 18.073 20.0613 18.0095C20.1739 16.0928 20.285 14.176 20.3975 12.2593C20.4747 10.9453 20.5534 9.63135 20.6321 8.31744C20.6344 8.27805 20.6353 8.23778 20.6394 8.19838C20.6426 8.16887 20.6474 8.13977 20.6574 8.11232C20.6711 8.0748 20.7379 8.02007 20.7662 8.0262C20.8193 8.03773 20.8884 8.08065 20.9028 8.1233C20.9277 8.19651 20.9216 8.27974 20.9183 8.35844C20.8876 9.09005 20.8605 9.82179 20.8213 10.553C20.7235 12.3812 20.6217 14.2093 20.5178 16.0373C20.4762 16.7684 20.424 17.499 20.3785 18.23C20.3726 18.3233 20.3775 18.4173 20.3776 18.5754V18.5796C20.5346 18.4698 20.6401 18.4042 20.735 18.3283C22.9201 16.5826 24.9259 14.6882 26.7589 12.648C26.7944 12.6085 26.8309 12.5698 26.8683 12.5315C26.8789 12.5206 26.89 12.51 26.9008 12.4992C26.9282 12.4718 26.9556 12.4443 26.9837 12.4174C26.9943 12.4072 27.0052 12.3973 27.016 12.3871C27.0454 12.3594 27.0749 12.3316 27.1049 12.3043C27.112 12.2979 27.1193 12.2915 27.1264 12.285C27.3254 12.1051 27.535 11.9327 27.7339 11.7529C27.7725 11.718 27.8168 11.6765 27.8662 11.6656C27.9233 11.6529 28.0273 11.6528 28.0428 11.679C28.0727 11.7293 28.061 11.8027 28.0531 11.8652C28.0502 11.8886 28.012 11.9105 27.9863 11.9292C27.2018 12.5001 26.6368 13.2449 25.9865 13.9214C24.9579 14.9911 23.9064 16.0408 22.7571 17.0107C22.2238 17.4608 21.6942 17.9144 21.1564 18.3603C20.9546 18.5277 20.7344 18.6775 20.4974 18.8541C20.8472 18.9675 21.1887 18.9685 21.5251 18.9872C22.8739 19.0621 24.1942 18.8908 25.5032 18.6193C26.3712 18.4392 27.2411 18.2665 28.1106 18.092C28.2459 18.0648 28.4279 17.9868 28.4702 18.1721C28.5021 18.312 28.3622 18.331 28.2413 18.3507C28.2365 18.3515 28.2316 18.3523 28.2268 18.3531C28.2154 18.3551 28.2041 18.357 28.1934 18.3591C26.9862 18.5976 25.7823 18.8504 24.571 19.0713C23.1947 19.3223 21.8085 19.3822 20.374 19.1282C20.3676 19.2723 20.3544 19.4044 20.357 19.5362C20.3607 19.7168 20.3669 19.8972 20.3743 20.0775C20.3874 20.0892 20.3999 20.1016 20.4136 20.1127C21.0235 20.6062 21.6355 21.0988 22.3341 21.495C22.749 21.7303 23.1866 21.9268 23.6649 22.0398C23.9423 22.1054 24.0733 22.0184 24.0725 21.7653C24.0718 21.5781 24.0477 21.3904 24.0232 21.2041C23.9977 21.01 23.9592 20.8172 23.927 20.6271C24.5018 20.652 25.034 20.6863 25.5667 20.6956C26.3357 20.7089 27.0097 20.4826 27.5836 20.0236C27.7063 19.9255 27.8293 19.8283 27.7311 19.6731C27.6222 19.501 27.7087 19.3861 27.8501 19.2905C28.0447 19.1589 28.2449 19.0322 28.4532 18.9186C28.7666 18.7479 29.0933 18.5963 29.4062 18.4249C29.5762 18.3318 29.7393 18.2242 29.8875 18.1055C30.0637 17.9643 30.03 17.8496 29.807 17.7918Z"
              fill="url(#paint0_linear_6855_24447)"
            />
          </g>
          <defs>
            <filter
              id="filter0_d_6855_24447"
              x="0"
              y="-0.00012207"
              width="40"
              height="40.1293"
              filterUnits="userSpaceOnUse"
              color-interpolation-filters="sRGB"
            >
              <feFlood flood-opacity="0" result="BackgroundImageFix" />
              <feColorMatrix
                in="SourceAlpha"
                type="matrix"
                values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                result="hardAlpha"
              />
              <feOffset dy="4" />
              <feGaussianBlur stdDeviation="5" />
              <feComposite in2="hardAlpha" operator="out" />
              <feColorMatrix
                type="matrix"
                values="0 0 0 0 0.968627 0 0 0 0 0.490196 0 0 0 0 0.12549 0 0 0 0.25 0"
              />
              <feBlend
                mode="normal"
                in2="BackgroundImageFix"
                result="effect1_dropShadow_6855_24447"
              />
              <feBlend
                mode="normal"
                in="SourceGraphic"
                in2="effect1_dropShadow_6855_24447"
                result="shape"
              />
            </filter>
            <linearGradient
              id="paint0_linear_6855_24447"
              x1="10"
              y1="16.0645"
              x2="30"
              y2="16.0645"
              gradientUnits="userSpaceOnUse"
            >
              <stop stop-color="#F12711" />
              <stop offset="0.1399" stop-color="#F24113" />
              <stop offset="0.4177" stop-color="#F37015" />
              <stop offset="0.6637" stop-color="#F49317" />
              <stop offset="0.8662" stop-color="#F5A719" />
              <stop offset="1" stop-color="#F5AF19" />
            </linearGradient>
          </defs>
        </svg>
      ),
      color: "#d97706",
    },
  };

  // toggle selection
  const handleChange = (value) => {
    setTests((prev) =>
      prev.includes(value) ? prev.filter((t) => t !== value) : [...prev, value],
    );
  };

  const handleDurationChange = (value) => {
    setDurations((prev) =>
      prev.includes(value) ? prev.filter((d) => d !== value) : [...prev, value],
    );
  };

  const handleStudyLevelChange = (value) => {
    setStudyLevel(value);

    if (value === "undergraduate") {
      const ugIds = courseLevels.underGraduate.map((lvl) => lvl.id);
      setSelectedLevels(ugIds);
      setCheckedLevels([]);
    }

    if (value === "postgraduate") {
      const pgIds = courseLevels.postGraduate.map((lvl) => lvl.id);
      setSelectedLevels(pgIds);
      setCheckedLevels([]);
    }
  };

  const handleCheckboxToggle = (id) => {
    setCheckedLevels((prev) =>
      prev.includes(id) ? prev.filter((c) => c !== id) : [...prev, id],
    );
  };

  const handleResetFilters = () => {
    // Reset states

    setBudget([3000, 99980]);
    setDurations([]);
    setCurrency("USD");
    setChecked({});
    setSearchTerm("");
    setStudyLevel("");
    setSelectedLevels([]);
    setYear([]);
    setIntake("");
    setTests([]);
    setCheckedLevels([]);
    setResetting(true);
  };

  useEffect(() => {
    if (resetting) {
      handleApplyFilters();
      setResetting(false);
    }
  }, [resetting]);

  return (
    <>
      {/* Dropdown trigger */}
      <Badge
        badgeContent={activeFilterCount}
        color="error"
        invisible={activeFilterCount === 0}
        overlap="circular"
        sx={{
          "& .MuiBadge-badge": {
            top: 1,
            right: 3,
            height: "20px",
            minWidth: "18px",
            fontSize: "11px",
            border: "2px solid red",
          },
        }}
      >
        <Button
          variant="outlined"
          size="small"
          onClick={handleOpen}
          className="font-inter"
          sx={{
            fontWeight: 600,
            fontStyle: "normal",
            fontSize: "16px",
            lineHeight: "100%",
            letterSpacing: "0px",
            textAlign: "center",
            verticalAlign: "middle",
            borderRadius: 2,
            width: "120px",
            height: "34px",
            color: "#572575",
            borderColor: "#572575",
            justifyContent: "space-between",
            textTransform: "none",
            "&:hover": {
              borderColor: "#7a3b9a",
              color: "#7a3b9a",
            },
          }}
        >
          Filters
          <svg
            width="20"
            height="18"
            viewBox="0 0 20 18"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M0.769231 4.64102H3.15487C3.51282 6.39384 5.06564 7.71794 6.92308 7.71794C8.78051 7.71794 10.3344 6.39384 10.6913 4.64102H19.2308C19.6554 4.64102 20 4.2964 20 3.87179C20 3.44717 19.6554 3.10256 19.2308 3.10256H10.6913C10.3333 1.34974 8.78051 0.0256348 6.92308 0.0256348C5.06564 0.0256348 3.5118 1.34974 3.15487 3.10256H0.769231C0.344615 3.10256 0 3.44717 0 3.87179C0 4.2964 0.344615 4.64102 0.769231 4.64102ZM6.92308 1.5641C8.1959 1.5641 9.23077 2.59999 9.23077 3.87179C9.23077 5.14358 8.1959 6.17948 6.92308 6.17948C5.65026 6.17948 4.61539 5.14358 4.61539 3.87179C4.61539 2.59999 5.65026 1.5641 6.92308 1.5641ZM19.2308 13.359H16.8451C16.4872 11.6061 14.9344 10.282 13.0769 10.282C11.2195 10.282 9.66564 11.6061 9.30872 13.359H0.769231C0.344615 13.359 0 13.7036 0 14.1282C0 14.5528 0.344615 14.8974 0.769231 14.8974H9.30872C9.66667 16.6502 11.2195 17.9744 13.0769 17.9744C14.9344 17.9744 16.4882 16.6502 16.8451 14.8974H19.2308C19.6554 14.8974 20 14.5528 20 14.1282C20 13.7036 19.6554 13.359 19.2308 13.359ZM13.0769 16.4359C11.8041 16.4359 10.7692 15.4 10.7692 14.1282C10.7692 12.8564 11.8041 11.8205 13.0769 11.8205C14.3497 11.8205 15.3846 12.8564 15.3846 14.1282C15.3846 15.4 14.3497 16.4359 13.0769 16.4359Z"
              fill="#572575"
            />
          </svg>
        </Button>
      </Badge>

      {/* Dropdown Menu */}
      <Drawer
        anchor="right"
        open={open}
        onClose={handleClose}
        PaperProps={{
          sx: {
            width: 488,
            borderRadius: "16px 0 0 16px",
            p: 1,
            maxHeight: "100vh",
          },
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column" }}>
          {/* Fixed Top Heading */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              p: 1,
              borderBottom: "1px solid #eee",
              position: "sticky",
              top: 0,
              bgcolor: "#fff",
              zIndex: 1,
            }}
          >
            <Typography
              sx={{
                fontFamily: "Inter",
                wordSpacing: "6px",
                fontWeight: 400,
                fontSize: "32px",
                color: "#572575",
              }}
            >
              Filters
            </Typography>

            <Grid>
              <IconButton
                onClick={handleClose}
                size="small"
                sx={{
                  color: "#572575",
                  "&:hover": { color: "#7a3b9a" },
                }}
              >
                <svg
                  width="26"
                  height="25"
                  viewBox="0 0 26 25"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M21.5743 3.37322C21.674 3.27357 21.7532 3.15525 21.8072 3.02501C21.8613 2.89477 21.8891 2.75516 21.8892 2.61415C21.8893 2.47314 21.8616 2.33349 21.8077 2.20319C21.7539 2.07288 21.6748 1.95446 21.5752 1.85469C21.4755 1.75492 21.3572 1.67575 21.227 1.62171C21.0967 1.56767 20.9571 1.53981 20.8161 1.53972C20.6751 1.53964 20.5355 1.56732 20.4052 1.62121C20.2749 1.67509 20.1564 1.75411 20.0567 1.85375L13.5812 8.32922L7.1076 1.85375C6.9061 1.65226 6.63282 1.53906 6.34786 1.53906C6.06291 1.53906 5.78962 1.65226 5.58813 1.85375C5.38664 2.05525 5.27344 2.32853 5.27344 2.61349C5.27344 2.89844 5.38664 3.17173 5.58813 3.37322L12.0636 9.84682L5.58813 16.3204C5.48836 16.4202 5.40922 16.5386 5.35522 16.669C5.30123 16.7993 5.27344 16.9391 5.27344 17.0802C5.27344 17.2212 5.30123 17.361 5.35522 17.4913C5.40922 17.6217 5.48836 17.7401 5.58813 17.8399C5.78962 18.0414 6.06291 18.1546 6.34786 18.1546C6.48896 18.1546 6.62867 18.1268 6.75903 18.0728C6.88938 18.0188 7.00783 17.9397 7.1076 17.8399L13.5812 11.3644L20.0567 17.8399C20.2582 18.0411 20.5313 18.1541 20.8161 18.1539C21.1009 18.1537 21.374 18.0404 21.5752 17.839C21.7764 17.6375 21.8894 17.3643 21.8892 17.0795C21.8891 16.7947 21.7758 16.5217 21.5743 16.3204L15.0988 9.84682L21.5743 3.37322Z"
                    fill="#555555"
                  />
                </svg>
              </IconButton>
            </Grid>
          </Box>
        </Box>

        <Box
          sx={{
            flex: 1,
            overflowY: "auto",
            "&::-webkit-scrollbar": {
              display: "none",
            },
            "-ms-overflow-style": "none",
            "scrollbar-width": "none",
          }}
        >
          {/* === Field of Study Accordion === */}
          <Accordion defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography
                fontFamily={"Inter"}
                fontWeight={700}
                fontSize={"18px"}
                color={"#333333"}
              >
                Field of Study
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              {/* Search Bar */}
              <TextField
                size="small"
                placeholder="Search field of study or narrow field"
                fullWidth
                sx={{ mb: 1 }}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />

              {/* Broad fields */}
              {filteredBroadFields.map((broad) => {
                const subFields = narrowFields[broad.id] || [];

                return (
                  <Box key={broad.id} sx={{}}>
                    {/* Broad Field as Dropdown */}
                    <Accordion
                      disableGutters
                      elevation={0}
                      sx={{
                        boxShadow: "none",
                        "&:before": { display: "none" },
                        background: "transparent",
                      }}
                      onChange={(e, expanded) => {
                        if (expanded && !narrowFields[broad.id]) {
                          fetchNarrowFields(broad.id);
                        }
                      }}
                    >
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography
                          sx={{
                            fontFamily: "Inter, sans-serif",
                            fontWeight: 500,
                            fontSize: "13px",
                          }}
                        >
                          {broad.broad_field_name}
                        </Typography>
                      </AccordionSummary>

                      <AccordionDetails sx={{ pl: 3 }}>
                        {subFields.map((nf) => (
                          <Grid
                            key={nf.id}
                            container
                            alignItems="center"
                            sx={{ mb: 0.5 }}
                          >
                            <Checkbox
                              sx={{ p: 0, mr: 1 }}
                              checked={!!checked[nf.narrow_field_name]}
                              onChange={handleChildChange(
                                broad.broad_field_name,
                                broad.id,
                                nf.narrow_field_name,
                                subFields,
                              )}
                            />
                            <Typography
                              sx={{
                                fontFamily: "Inter, sans-serif",
                                fontWeight: 400,
                                fontSize: "12px",
                              }}
                            >
                              {nf.narrow_field_name}
                            </Typography>
                          </Grid>
                        ))}
                      </AccordionDetails>
                    </Accordion>
                  </Box>
                );
              })}
            </AccordionDetails>
          </Accordion>

          {/* Level of study */}
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography
                fontFamily={"Inter"}
                fontWeight={700}
                fontSize={"18px"}
                color={"#333333"}
              >
                Level of Study
              </Typography>
            </AccordionSummary>

            <AccordionDetails>
              <RadioGroup
                row
                value={studyLevel}
                onChange={(e) => handleStudyLevelChange(e.target.value)}
              >
                <FormControlLabel
                  value="undergraduate"
                  control={
                    <Radio
                      sx={{
                        color: "#572575",
                        "&.Mui-checked": { color: "#572575" },
                      }}
                    />
                  }
                  label="Undergraduate"
                />
                <FormControlLabel
                  value="postgraduate"
                  control={
                    <Radio
                      sx={{
                        color: "#572575",
                        "&.Mui-checked": { color: "#572575" },
                      }}
                    />
                  }
                  label="Postgraduate"
                />
              </RadioGroup>

              <Grid sx={{ pl: 3, mt: 1 }}>
                {studyLevel &&
                  (studyLevel === "undergraduate"
                    ? courseLevels.underGraduate
                    : courseLevels.postGraduate
                  ).map((opt) => (
                    <FormControlLabel
                      key={opt.id}
                      control={
                        <Checkbox
                          checked={checkedLevels.includes(opt.id)}
                          onChange={() => handleCheckboxToggle(opt.id)}
                          sx={{
                            color: "#572575",
                            "&.Mui-checked": { color: "#572575" },
                          }}
                        />
                      }
                      label={opt.course_level}
                      sx={{ display: "block", mb: 0.5 }}
                    />
                  ))}
              </Grid>
            </AccordionDetails>
          </Accordion>

          {/* === Annual Budget Accordion === */}
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography
                fontFamily={"Inter"}
                fontWeight={700}
                fontSize={"18px"}
                color={"#333333"}
              >
                Annual Budget
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              {/* Currency Select */}
              <Grid
                container
                justifyContent="space-between"
                alignItems="center"
              >
                <Typography
                  sx={{
                    fontFamily: "Inter",
                    fontWeight: 400,
                    fontStyle: "normal",
                    fontSize: "18px",
                    lineHeight: "100%",
                    letterSpacing: "0em",
                    verticalAlign: "middle",
                  }}
                >
                  Select Currency
                </Typography>
              </Grid>

              {/* Budget Slider */}
              <Slider
                value={budget}
                onChange={(e, val) => setBudget(val)}
                valueLabelDisplay="auto"
                min={1000}
                max={50000}
                sx={{ color: "#fbc02d" }}
              />

              {/* From / To Inputs */}
              <Grid container justifyContent="space-between" mt={2}>
                <TextField
                  size="small"
                  value={budget[0]}
                  onChange={(e) => setBudget([+e.target.value, budget[1]])}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment
                        position="start"
                        sx={{ color: "#572575", fontWeight: 600 }}
                      >
                        <BiDollar />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    width: 120,
                    backgroundColor: "#F9F4FB",
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "8px",
                      "& fieldset": {
                        borderColor: "#572575",
                        borderWidth: "2px",
                      },
                      "&:hover fieldset": {
                        borderColor: "#572575",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#572575",
                      },
                    },
                    "& .MuiInputBase-input": {
                      color: "#572575",
                      fontWeight: "600",
                      fontSize: "18px",
                      textAlign: "center",
                    },
                  }}
                />

                <TextField
                  size="small"
                  value={budget[1]}
                  onChange={(e) => setBudget([budget[0], +e.target.value])}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment
                        position="start"
                        sx={{ color: "#572575", fontWeight: 600 }}
                      >
                        <BiDollar />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    width: 120,
                    backgroundColor: "#F9F4FB",
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "8px",
                      "& fieldset": {
                        borderColor: "#572575",
                        borderWidth: "2px",
                      },
                      "&:hover fieldset": {
                        borderColor: "#572575",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#572575",
                      },
                    },
                    "& .MuiInputBase-input": {
                      color: "#572575",
                      fontWeight: "600",
                      fontSize: "18px",
                      textAlign: "center",
                    },
                  }}
                />
              </Grid>
            </AccordionDetails>
          </Accordion>

          {/* Intake Period Accordian */}
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography
                fontFamily={"Inter"}
                fontWeight={700}
                fontSize={"18px"}
                color={"#333333"}
              >
                Intake Period
              </Typography>
            </AccordionSummary>

            <AccordionDetails>
              {/* Year Select */}
              <Grid
                container
                justifyContent="space-between"
                alignItems="center"
                mb={2}
              >
                <Typography
                  sx={{
                    fontFamily: "Inter",
                    fontWeight: 400,
                    fontSize: "18px",
                  }}
                >
                  Select Year
                </Typography>

                <Select
                  size="small"
                  value={year}
                  onChange={(e) => setYear(e.target.value)}
                  sx={{
                    minWidth: 106,
                    height: 26,
                    fontFamily: "Inter",
                    fontWeight: 700,
                    fontSize: "16px",
                    borderRadius: "8px",
                    backgroundColor: "#FDF3FD",
                    "& .MuiOutlinedInput-notchedOutline": {
                      border: "none",
                    },
                    "& .MuiSelect-select": {
                      display: "flex",
                      alignItems: "center",
                      pl: 1.2,
                      pr: 3,
                      color: "#572575",
                    },
                    "& .MuiSelect-icon": {
                      color: "#572575",
                      right: 8,
                    },
                  }}
                >
                  {Array.from({ length: 6 }, (_, i) => {
                    const yr = new Date().getFullYear() + i;
                    return (
                      <MenuItem key={yr} value={yr}>
                        {yr}
                      </MenuItem>
                    );
                  })}
                </Select>
              </Grid>

              {/* Intakes Grid from API */}
              <Grid container spacing={2}>
                {intakes.map((s) => {
                  const meta = intakeMeta[s.intake] || {
                    icon: null,
                    color: "#572575",
                  };
                  return (
                    <Grid item xs={6} key={s.id}>
                      <Box
                        onClick={() => setIntake(s.month)}
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          width: "100%",
                          borderRadius: "12px",
                          p: 1.5,
                          cursor: "pointer",
                          transition: "0.2s",
                          backgroundColor:
                            intake === s.month ? "#cac7cfff" : "#F9FAFB",
                          "&:hover": {
                            boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
                          },
                        }}
                      >
                        {/* Left: icon + label */}
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          {meta.icon}
                          <Typography
                            sx={{
                              fontFamily: "Inter",
                              fontWeight: 500,
                              fontSize: 14,
                            }}
                          >
                            {s.intake}
                          </Typography>
                        </Box>

                        {/* Right: months badge */}
                        <Box
                          sx={{
                            bgcolor: `${meta.color}22`,
                            px: 1.5,
                            py: 0.5,
                            borderRadius: "6px",
                          }}
                        >
                          <Typography
                            sx={{
                              fontWeight: 600,
                              fontSize: 14,
                              color: meta.color,
                            }}
                          >
                            {s.description}
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                  );
                })}
              </Grid>
            </AccordionDetails>
          </Accordion>

          {/* === Duration Accordion === */}
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography
                fontFamily={"Inter"}
                fontWeight={700}
                fontSize={"18px"}
                color={"#333333"}
              >
                Duration
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={1}>
                {durationOptions.map((opt) => (
                  <Grid item xs={3} key={opt.value}>
                    <FormGroup>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={durations.includes(opt.value)}
                            onChange={() => handleDurationChange(opt.value)}
                            sx={{
                              color: "#572575",
                              "&.Mui-checked": { color: "#572575" },
                            }}
                          />
                        }
                        label={
                          <Typography
                            sx={{
                              fontFamily: "Inter",
                              fontWeight: 400,
                              fontSize: "14px",
                              textAlign: "center",
                              textWrap: "nowrap",
                            }}
                          >
                            {opt.label}
                          </Typography>
                        }
                      />
                    </FormGroup>
                  </Grid>
                ))}
              </Grid>
            </AccordionDetails>
          </Accordion>

          {/* === Entrance Test Accordion === */}
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography
                fontFamily={"Inter"}
                fontWeight={700}
                fontSize={"18px"}
                color={"#333333"}
              >
                Entrance Test
              </Typography>
            </AccordionSummary>

            <AccordionDetails>
              <Grid container spacing={1}>
                {testOptions.map((opt) => (
                  <Grid item xs={3} key={opt}>
                    <FormGroup>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={tests.includes(opt)}
                            onChange={() => handleChange(opt)}
                            sx={{
                              color: "#572575",
                              "&.Mui-checked": { color: "#572575" },
                            }}
                          />
                        }
                        label={
                          <Typography
                            sx={{
                              fontFamily: "Inter",
                              fontWeight: 400,
                              fontSize: "14px",
                              lineHeight: "100%",
                              textAlign: "center",
                            }}
                          >
                            {opt}
                          </Typography>
                        }
                      />
                    </FormGroup>
                  </Grid>
                ))}
              </Grid>
            </AccordionDetails>
          </Accordion>
        </Box>
        {/* Apply Button */}
        <MenuItem disableRipple sx={{ justifyContent: "center" }}>
          <Grid container spacing={2} justifyContent="center">
            {/* Reset Button */}
            <Grid item>
              <Button
                variant="outlined"
                color="secondary"
                onClick={handleResetFilters}
                disabled={loading}
                sx={{
                  width: { xs: "120px", sm: "140px", md: "150px" },
                  borderRadius: "8px",
                  color: "#572575",
                  borderColor: "#572575",
                  fontWeight: 600,
                  "&:hover": {
                    borderColor: "#7a3b9a",
                    color: "#7a3b9a",
                    backgroundColor: "whitesmoke",
                  },
                }}
              >
                Reset
              </Button>
            </Grid>

            {/* Apply Button */}
            <Grid item>
              <Button
                onClick={handleApplyFilters}
                sx={{
                  width: { xs: "120px", sm: "140px", md: "150px" },
                  backgroundColor: "#572575",
                  borderRadius: "8px",
                  color: "white",
                  fontWeight: 600,
                  "&:hover": { backgroundColor: "#7a3b9a" },
                }}
              >
                {loading ? "Loading..." : "Apply"}
              </Button>
            </Grid>
          </Grid>
        </MenuItem>
      </Drawer>
    </>
  );
}
